import React, { useState, useEffect } from 'react';
import { Table, Select, Typography, Spin, Row, Col } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

const { Title } = Typography;
const { Option } = Select;

// GraphQL query to get payments by status
export const PAYMENTS_BY_STATUS = gql`
  query PaymentsByStatus(
    $status: [String!]!
    $limit: Int
    $page: Int
    $startDate: DateTime
    $endDate: DateTime
  ) {
    paymentsByStatus(
      status: $status
      limit: $limit
      page: $page
      startDate: $startDate
      endDate: $endDate
    ) {
      items {
        id
        amount
        payByDate
        account {
          providerName
          accountNumber
        }
        serviceCategoryName
        bankAccount {
          accountNumber
        }
      }
      count
    }
  }
`;

interface Payment {
  id: string;
  amount: number;
  payByDate: string;
  account: {
    providerName: string;
    accountNumber: string;
  };
  serviceCategoryName: string;
  bankAccount: {
    accountNumber: string;
  };
}

interface PaymentsByStatusData {
  paymentsByStatus: {
    items: Payment[];
    count: number;
  };
}

interface PaymentsByStatusVars {
  status: string[];
  limit?: number;
  page?: number;
  startDate?: string;
  endDate?: string;
}

const Reports: React.FC = () => {
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState<number>(currentDate.getMonth() + 1); // JavaScript months are 0-indexed
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());

  // Calculate date range for the selected month/year
  const getDateRange = (month: number, year: number) => {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const { startDate, endDate } = getDateRange(selectedMonth, selectedYear);


  const { loading, error, data, refetch } = useQuery<PaymentsByStatusData, PaymentsByStatusVars>(
    PAYMENTS_BY_STATUS,
    {
      variables: {
        status: ['completed'],
        limit: 50,
        page: 1,
        startDate,
        endDate,
      },
      onCompleted: (data) => {
        // Query completed successfully
      },
      onError: (error) => {
        console.error('Reports GraphQL Query Error:', error);
        console.error('Error Details:', {
          message: error.message,
          graphQLErrors: error.graphQLErrors,
          networkError: error.networkError,
        });
      },
    }
  );

  // Refetch data when month or year changes
  useEffect(() => {
    const { startDate: newStartDate, endDate: newEndDate } = getDateRange(selectedMonth, selectedYear);
    refetch({
      status: ['completed'],
      limit: 50,
      page: 1,
      startDate: newStartDate,
      endDate: newEndDate,
    });
  }, [selectedMonth, selectedYear, refetch]);

  const getLastFourDigits = (accountNumber: string) => {
    if (!accountNumber) return 'N/A';
    return accountNumber.slice(-4);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Parse the date string and format it without timezone conversion issues
    const date = new Date(dateString);
    // Use UTC methods to avoid timezone conversion
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    
    return `${month}/${day}/${year}`;
  };

  // Generate month options
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Generate year options (current year and previous 5 years)
  const yearOptions = [];
  for (let i = 0; i < 6; i++) {
    const year = currentDate.getFullYear() - i;
    yearOptions.push({ value: year, label: year.toString() });
  }

  const columns: ColumnsType<Payment> = [
    {
      title: 'Provider Name',
      dataIndex: ['account', 'providerName'],
      key: 'providerName',
      render: (text, record) => {
        return text || 'N/A';
      },
      sorter: (a, b) => {
        const nameA = a.account.providerName || '';
        const nameB = b.account.providerName || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Account Number (last 4)',
      key: 'accountNumber',
      render: (_, record) => {
        // Try both bankAccount.accountNumber and account.accountNumber
        const bankAccountNumber = record.bankAccount?.accountNumber;
        const accountNumber = record.account?.accountNumber;
        return getLastFourDigits(bankAccountNumber || accountNumber);
      },
      sorter: (a, b) => {
        const accountA = (a.bankAccount?.accountNumber || a.account?.accountNumber) || '';
        const accountB = (b.bankAccount?.accountNumber || b.account?.accountNumber) || '';
        return accountA.localeCompare(accountB);
      },
    },
    {
      title: 'Category',
      dataIndex: 'serviceCategoryName',
      key: 'category',
      render: (text, record) => {
        return text || 'N/A';
      },
      sorter: (a, b) => {
        const categoryA = a.serviceCategoryName || '';
        const categoryB = b.serviceCategoryName || '';
        return categoryA.localeCompare(categoryB);
      },
    },
    {
      title: 'Payment Date',
      dataIndex: 'payByDate',
      key: 'paymentDate',
      render: (date, record) => {
        return formatDate(date);
      },
      sorter: (a, b) => new Date(a.payByDate).getTime() - new Date(b.payByDate).getTime(),
    },
    {
      title: 'Payment Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => {
        return formatCurrency(amount);
      },
      sorter: (a, b) => a.amount - b.amount,
    },

  ];

  const completedPayments = data?.paymentsByStatus?.items || [];
  const totalCount = data?.paymentsByStatus?.count || 0;

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />;
  if (error) return <p>Error loading payment reports.</p>;

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Reports</Title>
      
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col>
          <span style={{ marginRight: 8, fontWeight: 'bold' }}>Year:</span>
          <Select
            value={selectedYear}
            onChange={setSelectedYear}
            style={{ width: 120 }}
          >
            {yearOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <span style={{ marginRight: 8, fontWeight: 'bold' }}>Month:</span>
          <Select
            value={selectedMonth}
            onChange={setSelectedMonth}
            style={{ width: 140 }}
          >
            {monthOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {completedPayments.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '50px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '8px',
          color: '#666'
        }}>
          <Title level={4} style={{ color: '#666' }}>
            No payments completed for this month.
          </Title>
        </div>
      ) : (
        <Table
          dataSource={completedPayments}
          columns={columns}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payments`,
            pageSizeOptions: ['10', '20', '50', '100'],
            total: totalCount,
          }}
        />
      )}
    </div>
  );
};

export default Reports;
